import * as React from "react";

import { cn } from "@/lib/utils";

interface GradientOutlineButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onClick"> {
  children: React.ReactNode;
  width?: number;
  height?: number;
  radius?: number;
  blur?: number;
  stroke?: number;
  from?: string;
  to?: string;
}

export function GradientOutlineButton({
  children,
  className,
  width = 170,
  height = 48,
  radius = 22,
  stroke = 1,
  blur = 22,
  from = "#FFB929",
  to = "#FF2F9C",
  ...props
}: GradientOutlineButtonProps) {
  /* --- geometry -------------------------------------------------------- */
  const fixedW = Math.floor(width);
  const fixedH = Math.floor(height);
  const fixedR = Math.floor(radius);
  const fixedS = Math.max(1, Math.floor(stroke));

  const offset = fixedS / 2;
  const innerW = fixedW - fixedS;
  const innerH = fixedH - fixedS;
  const rInner = Math.max(fixedR - offset, 0);

  /* unique ids so multiple buttons don’t clash */
  const uid = React.useId();
  const gradId = `${uid}-grad`;
  const filterId = `${uid}-filter`;

  return (
    <button
      {...props}
      className={cn(
        "relative inline-flex items-center justify-center overflow-hidden select-none",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        /* hover / active – CSS-only */
        "transition-transform duration-200 ease-out",
        "hover:-translate-y-[2px] hover:shadow-[0_4px_14px_rgba(0,0,0,0.35)]",
        "active:translate-y-0 active:shadow-[0_0px_6px_rgba(0,0,0,0.20)]",
        className
      )}
      style={{ width: fixedW, height: fixedH, borderRadius: fixedR }}
    >
      {/* glass fill ------------------------------------------------------- */}
      <div
        aria-hidden
        className="absolute pointer-events-none backdrop-blur[22]"
        style={{
          inset: fixedS,
          borderRadius: rInner,
          backdropFilter: `blur(${blur}px)`,
          WebkitBackdropFilter: `blur(${blur}px)`,
          background:
            "linear-gradient(90deg,#010C15 0%,rgba(1,12,21,0.06) 50%,#010C15 100%)",
        }}
      />

      {/* gradient outline ------------------------------------------------- */}
      <svg
        aria-hidden
        focusable="false"
        className="absolute inset-0 pointer-events-none"
        width={fixedW}
        height={fixedH}
        viewBox={`0 0 ${fixedW} ${fixedH}`}
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={from} />
            <stop offset="100%" stopColor={to} />
          </linearGradient>

          <filter id={filterId}>
            <feGaussianBlur in="SourceGraphic" stdDeviation="0.7" result="b" />
            <feComponentTransfer in="b">
              <feFuncA type="linear" slope="3" />
            </feComponentTransfer>
          </filter>
        </defs>

        <rect
          x={offset}
          y={offset}
          width={innerW}
          height={innerH}
          rx={rInner}
          ry={rInner}
          stroke={`url(#${gradId})`}
          strokeWidth={fixedS}
          fill="none"
          vectorEffect="non-scaling-stroke"
          shapeRendering="geometricPrecision"
          filter={`url(#${filterId})`}
        />
      </svg>

      {/* label ------------------------------------------------------------ */}
      <span className="relative z-10 text-button uppercase font-semibold">
        {children}
      </span>
    </button>
  );
}
